/**
 * Extras — admin housekeeping that doesn't belong on the Tests or Tracker
 * tabs: the Tier 1 category lookup (`tier1Routes.ts`'s own category routes —
 * the extension's side panel reads the same list to populate its category
 * field) and a plain, written-down list of tests QA knows cannot be
 * automated (avatar lip-sync, AI lesson generation, …), so the reason a
 * manual test stays manual doesn't need re-discovering every regression
 * cycle.
 */
import { useEffect, useState } from 'react';
import { api, ApiError, type Tier1Category, type NonAutomatableTest } from '../api';

export function ExtrasScreen() {
  return (
    <div className="flex flex-col gap-6 p-6">
      <h1 className="text-lg font-semibold">Extras</h1>
      <CategoriesPanel />
      <NonAutomatablePanel />
    </div>
  );
}

function CategoriesPanel() {
  const [categories, setCategories] = useState<Tier1Category[]>([]);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.tier1Categories().then((r) => setCategories(r.categories)).catch((e) => setError(String(e)));
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await api.createTier1Category(name);
      setNewName('');
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (category: Tier1Category) => {
    const name = window.prompt('Rename this category to:', category.name);
    if (!name || !name.trim() || name.trim() === category.name) return;
    try {
      await api.renameTier1Category(category.id, name.trim());
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const remove = async (category: Tier1Category) => {
    if (!window.confirm(`Delete category "${category.name}"? Recordings already filed under it keep the name — this only removes it from the list offered when saving a new one.`)) return;
    try {
      await api.deleteTier1Category(category.id);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="surface flex flex-col gap-3 p-5" data-testid="extras-categories">
      <div>
        <h2 className="text-base font-semibold">Tier 1 categories</h2>
        <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
          The list offered when saving a recorded test through the extension — "Login page",
          "Course page", and so on. Add or rename as the app's own pages change.
        </p>
      </div>

      {error && <p className="text-sm" style={{ color: 'hsl(var(--fail))' }}>{error}</p>}

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <span key={c.id} className="pill flex items-center gap-2" data-testid={`extras-category-${c.id}`}>
            {c.name}
            <button className="opacity-60 hover:opacity-100" onClick={() => rename(c)} title="Rename" aria-label={`Rename ${c.name}`}>✎</button>
            <button className="opacity-60 hover:opacity-100" style={{ color: 'hsl(var(--fail))' }} onClick={() => remove(c)} title="Delete" aria-label={`Delete ${c.name}`}>✕</button>
          </span>
        ))}
        {categories.length === 0 && <p className="text-sm" style={{ color: 'hsl(var(--text-faint))' }}>No categories yet.</p>}
      </div>

      <div className="flex gap-2">
        <input
          className="field"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          placeholder="New category name"
          data-testid="extras-category-new-name"
        />
        <button className="btn btn-primary" onClick={add} disabled={busy || !newName.trim()} data-testid="extras-category-add">
          Add
        </button>
      </div>
    </div>
  );
}

function NonAutomatablePanel() {
  const [tests, setTests] = useState<NonAutomatableTest[]>([]);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.nonAutomatableTests().then((r) => setTests(r.tests)).catch((e) => setError(String(e)));
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createNonAutomatableTest({ name: name.trim(), category: category.trim() || undefined, notes: notes.trim() || undefined });
      setName('');
      setCategory('');
      setNotes('');
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (test: NonAutomatableTest) => {
    if (!window.confirm(`Remove "${test.name}" from this list?`)) return;
    try {
      await api.deleteNonAutomatableTest(test.id);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="surface flex flex-col gap-3 p-5" data-testid="extras-non-automatable">
      <div>
        <h2 className="text-base font-semibold">Non-automatable tests</h2>
        <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
          Things QA has decided cannot be automated — avatar lip-sync, AI lesson generation, and
          the like — written down here so they are still tested, just manually, and nobody has
          to rediscover why the next time coverage is reviewed.
        </p>
      </div>

      {error && <p className="text-sm" style={{ color: 'hsl(var(--fail))' }}>{error}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr style={{ color: 'hsl(var(--text-faint))' }}>
            <th className="p-2 text-left">Test</th>
            <th className="p-2 text-left">Category</th>
            <th className="p-2 text-left">Notes</th>
            <th className="p-2 text-left">Added by</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {tests.map((t) => (
            <tr key={t.id} style={{ borderTop: '1px solid hsl(var(--border))' }} data-testid={`extras-non-automatable-${t.id}`}>
              <td className="p-2 font-medium">{t.name}</td>
              <td className="p-2">{t.category || '—'}</td>
              <td className="p-2" style={{ color: 'hsl(var(--text-muted))' }}>{t.notes || '—'}</td>
              <td className="p-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{t.createdBy}</td>
              <td className="p-2 text-right">
                <button onClick={() => remove(t)} title="Remove" aria-label={`Remove ${t.name}`} style={{ color: 'hsl(var(--fail))' }}>✕</button>
              </td>
            </tr>
          ))}
          {tests.length === 0 && (
            <tr><td className="p-2 text-sm" colSpan={5} style={{ color: 'hsl(var(--text-faint))' }}>Nothing listed yet.</td></tr>
          )}
        </tbody>
      </table>

      <div className="flex flex-wrap gap-2">
        <input className="field flex-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Test name (e.g. Avatar lip-sync matches audio)" data-testid="extras-non-automatable-name" />
        <input className="field" style={{ maxWidth: '10rem' }} value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" data-testid="extras-non-automatable-category" />
        <input className="field flex-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why it can't be automated" data-testid="extras-non-automatable-notes" />
        <button className="btn btn-primary" onClick={add} disabled={busy || !name.trim()} data-testid="extras-non-automatable-add">
          Add
        </button>
      </div>
    </div>
  );
}
